import { Link } from "react-router-dom";
import { Pct } from "@/components/layernav/primitives";
import { fmtBig, sectorTrack } from "./SectorHero";
import { ProtocolLogo } from "./ProtocolLogo";
import { entityNavRoute, entityTrack, resolveEntity, useEntityRegistry } from "@/lib/entities";

/* Official protocol identity chips for article pages — live TVL, real mentions only.
   STRICT navigation truth (Rule 6): live registry entities link to their
   Entity Page; non-live identities render as inert informational chips. */
export const ProtocolMentionsStrip = ({ items }: { items: any[] }) => {
  const { data: reg } = useEntityRegistry();
  if (!items?.length) return null;
  return (
    <div className="mt-8" data-testid="protocol-mentions">
      <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.25em] text-[#00E5FF]">
        Protocols in this story <span className="text-gray-500 font-normal tracking-[0.15em]">· Live TVL · DeFiLlama</span>
      </p>
      <div className="flex flex-wrap gap-2 mt-2.5">
        {items.map((p) => {
          const route = entityNavRoute(resolveEntity(reg, "protocol", p.slug));
          const stats = (
            <>
              <span className="font-mono text-[10.5px] text-gray-400">{fmtBig(p.tvl_usd)}</span>
              {p.tvl_change_24h_pct != null && <Pct v={p.tvl_change_24h_pct} className="!text-[10.5px]" />}
            </>
          );
          if (!route) {
            return (
              <span key={p.slug} data-testid={`protocol-mention-${p.slug}`} data-live="false"
                className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] pl-1 pr-3 py-1">
                <ProtocolLogo slug={p.slug} name={p.protocol} size={24} />
                <span className="font-bold text-[12.5px] text-white">{p.protocol}</span>
                {stats}
              </span>
            );
          }
          return (
            <Link key={p.slug} to={route} data-live="true"
              data-testid={`protocol-mention-${p.slug}`}
              aria-label={`${p.protocol} — Entity Intelligence`}
              onClick={() => entityTrack("entity_nav_click", `protocol:${p.slug}`, { surface: "article_protocol_mentions" })}
              className="group inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] pl-1 pr-3 py-1 hover:border-[#00E5FF]/50 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60">
              <ProtocolLogo slug={p.slug} name={p.protocol} size={24} />
              <span className="font-bold text-[12.5px] text-white group-hover:text-[#00E5FF] transition-colors">{p.protocol}</span>
              {stats}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* Search: rich card when the query itself matches a protocol.
   Live entity → canonical Entity Page; non-live → honest Rankings link
   (this is the Search experience itself — destination is labeled truthfully). */
export const ProtocolMatchCard = ({ p }: { p: any }) => {
  const { data: reg } = useEntityRegistry();
  if (!p) return null;
  const route = entityNavRoute(resolveEntity(reg, "protocol", p.slug));
  return (
    <Link to={route || "/defi/protocols"}
      data-testid="search-protocol-match" data-live={route ? "true" : "false"}
      onClick={() => route
        ? entityTrack("entity_nav_click", `protocol:${p.slug}`, { surface: "search_protocol_match" })
        : sectorTrack("market_module_interaction", "search_protocol_match", "defi")}
      className="group flex items-center gap-4 rounded-xl border border-[#00E5FF]/25 bg-[#0a1114] p-4 mb-10 max-w-2xl hover:border-[#00E5FF]/50 transition-colors">
      <ProtocolLogo slug={p.slug} name={p.protocol} size={48} />
      <span className="min-w-0 flex-1">
        <span className="font-mono text-[8.5px] font-bold uppercase tracking-[0.25em] text-[#00E5FF]">Protocol Match</span>
        <span className="block font-heading font-bold text-white text-lg leading-tight">{p.protocol}</span>
        <span className="block font-mono text-[9.5px] uppercase tracking-wide text-gray-500 mt-0.5 truncate">
          {p.category}{p.dominant_chain ? ` · ${p.dominant_chain}` : ""}
          {p.token ? ` · ${p.token.symbol}` : ""}
        </span>
      </span>
      <span className="text-right shrink-0">
        <span className="block font-mono text-[15px] font-bold text-white">{fmtBig(p.tvl_usd)}</span>
        <span className="flex items-center justify-end gap-1.5">
          {p.tvl_change_24h_pct != null && <Pct v={p.tvl_change_24h_pct} className="!text-[11px]" />}
          <span className="font-mono text-[8.5px] uppercase text-gray-500">24H TVL</span>
        </span>
        <span className="block font-mono text-[10px] font-bold text-[#34D399] group-hover:text-white transition-colors mt-1">{route ? "Entity Intelligence →" : "Rankings →"}</span>
      </span>
    </Link>
  );
};

/* Search result cards: small official-logo cluster over the card art */
export const MentionBadges = ({ items, breaking = false }: { items: any[]; breaking?: boolean }) => {
  if (!items?.length) return null;
  return (
    <span aria-hidden data-testid="mention-badges"
      className={`absolute left-3 z-10 flex -space-x-1.5 pointer-events-none ${breaking ? "top-10" : "top-3"}`}>
      {items.slice(0, 3).map((p) => (
        <ProtocolLogo key={p.slug} slug={p.slug} name={p.protocol} size={22}
          className="!bg-[#0b0c10] shadow-lg" />
      ))}
    </span>
  );
};
